/*
 *  Copyright © 2025 Cenozon Inc. All Rights Reserved.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
   authenticateUser,
   initAuthenticationContext,
} from '@cenozon/indoraptor-user-auth-react-spa';
import { AuthenticationContext as IndoraptorAuthContext }
   from '@cenozon/indoraptor-user-auth-react';
import { createBrowserHistory } from 'history';
import { returnUri } from './utils/returnUri';
import App from './App';
import store from './store';

// Create a client for React Query
const queryClient = new QueryClient({
   defaultOptions: {
      queries: {
         staleTime: 30 * 1000, // 30 seconds default
         retry: 1,
         refetchOnWindowFocus: false,
      },
   },
});

const PORTAL = (process.env.REACT_APP_INDORAPTOR_PORTAL_URL ||
   'https://portal.cenozon.com/').replace(/\/?$/, '/');
const AUTH_URL = `${PORTAL}auth`; // no trailing slash
const CLIENT_ID = 'indoraptor';

const config = {
   applicationName: 'ILI',
   strictModeEnabled: false,
   authenticationConfig: {
      publicUrl:   AUTH_URL,
      internalUrl: AUTH_URL,
      providerUrl: AUTH_URL,
      clientId:    CLIENT_ID,
      redirectUri: `${window.location.origin}/`,
      nextUri:     next => next ?? returnUri(),
   },
};

const domEl = document.getElementById('root');
const contextProps = initAuthenticationContext(config.authenticationConfig);
const root = createRoot(domEl);

authenticateUser(contextProps, createBrowserHistory())
   .then(token => {
      root.render(
         <IndoraptorAuthContext.Provider value={contextProps}>
            <Provider store={store}>
               <QueryClientProvider client={queryClient}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     {token == null ? null : <App />}
                  </LocalizationProvider>
               </QueryClientProvider>
            </Provider>
         </IndoraptorAuthContext.Provider>
      );
   })
   .catch(err => {
      console.error('[indoraptor] authenticateUser threw:', err);
      root.render(<div>Authentication failed: {String(err)}</div>);
   });
